import { logInfo } from 'setup/logger/logInfo';
import { withTimeout } from 'shared/utils/withTimeout';
import { markServiceWorkerStale } from 'shared/channels/ensureServiceWorkerAwake';
import { alignServiceWorkerWithPage, isServiceWorkerAlignmentFailed } from '../alignment';
import { describeServiceWorkerPresence } from './model';
import { ServiceWorkerPresence } from './types';

/** What `vite-plugin-pwa` builds `src/sw.ts` to, and registers at the root scope. */
export const SERVICE_WORKER_SCRIPT_URL = '/sw.js';
/** How long a registered worker gets to become active. */
export const SERVICE_WORKER_ACTIVATION_TIMEOUT_MS = 10_000;

/**
 * Registers the worker again, the way the page did at its start, and brings it
 * to the page's version. Resolves whether the page has a worker it can use, or
 * one that is still installing (a slow network): it becomes active by itself,
 * so the page is not replaced by the refresh screen for it. Never rejects.
 */
export async function restoreServiceWorker(container: ServiceWorkerContainer): Promise<boolean> {
  try {
    const registration = await container.register(SERVICE_WORKER_SCRIPT_URL, { scope: '/' });
    const activeRegistration = await withTimeout(container.ready, SERVICE_WORKER_ACTIVATION_TIMEOUT_MS, () => null);
    if (!activeRegistration?.active) {
      const isInstalling = describeServiceWorkerPresence(registration) === ServiceWorkerPresence.present;
      if (isInstalling) {
        logInfo('service-worker-restored', { isActive: false });
      }
      return isInstalling;
    }
    // The worker may be the answer of a newer deploy than the page.
    const { alignment } = await alignServiceWorkerWithPage();
    if (isServiceWorkerAlignmentFailed(alignment)) {
      return false;
    }
  } catch {
    return false;
  }
  // Its start is not the one the last ping saw.
  markServiceWorkerStale();
  logInfo('service-worker-restored', { isActive: true });
  return true;
}
